import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Input from '@amzn/awsui-components-core/input';
import Modal from '@amzn/awsui-components-core/modal';
import Spinner from '@amzn/awsui-components-core/spinner';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetColumnHierarchy } from 'src/components/star-grid/datasets/datasetTypes';
import { filterHierarchyPairs } from 'src/components/star-grid/datasets/utils/datasetHierarchy';
import { useTextQuery } from 'src/components/star-grid/datasets/hooks/useTextQuery';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';

interface Props {
  readonly text: StarGridText;
  readonly pairs: readonly DatasetColumnHierarchy[];
  readonly loading: boolean;
  readonly onPick: (pair: DatasetColumnHierarchy) => void;
  readonly onDismiss: () => void;
}

/** A searchable list of every `(dimension, hierarchy)` pair, for mapping a dimension the profiler did not match. */
export const HierarchyChooserModal: React.FC<Props> = ({ text, pairs, loading, onPick, onDismiss }) => {
  const { query, setQuery } = useTextQuery();
  const matches = filterHierarchyPairs(pairs, query);
  return (
    <Modal visible header={text('wizardHierarchyChooseTitle')} onDismiss={onDismiss} data-testid="hierarchy-chooser">
      <Input
        type="search"
        value={query}
        placeholder={text('wizardHierarchySearchPlaceholder')}
        ariaLabel={text('wizardHierarchyChooseTitle')}
        onChange={({ detail }) => setQuery(detail.value)}
      />
      {loading ? (
        <Box margin={{ top: 'm' }}>
          <Spinner /> {text('wizardHierarchyLoading')}
        </Box>
      ) : matches.length === 0 ? (
        <Box margin={{ top: 'm' }} color="text-body-secondary">
          {text('wizardHierarchyNoMatch')}
        </Box>
      ) : (
        <div className={styles.sourceList}>
          {matches.map((pair) => (
            <button
              key={`${pair.dimension}::${pair.hierarchy}`}
              type="button"
              data-testid="hierarchy-pair"
              className={styles.sourceRow}
              onClick={() => onPick(pair)}
            >
              <strong>{pair.hierarchy}</strong>
              <span className={styles.sourceRef}>{pair.dimension}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
};
